import type { IDateFilterParams, INumberFilterParams, ISetFilterParams, ITextFilterParams } from "ag-grid-community";
import moment from "moment";
import { ColorCircle, ColorLabel, ColorPickerWrapper } from "presentation/view/components/styled/NbisTable.styled";
import type { NbisTableProps } from "presentation/view/components/TableWrapper/NbisTableConstant";
import { ColorPicker, IconButton } from "veronica-ui-component/dist/component/core";


// Normalize object-like cell values, e.g. { value, isWarning }.
export const getCellBaseValue = (cellValue: any) => {
    if (
        cellValue
        && typeof cellValue === 'object'
        && !Array.isArray(cellValue)
        && Object.prototype.hasOwnProperty.call(cellValue, 'value')
    ) {
        return cellValue.value;
    }
    return cellValue;
};

export const getCellBaseIsWarning = (cellValue: any) => {
    if (
        cellValue
        && typeof cellValue === 'object'
        && !Array.isArray(cellValue)
        && Object.prototype.hasOwnProperty.call(cellValue, 'isWarning')
    ) {
        return cellValue.isWarning;
    }
    return false;
};

export const getCellDisplayText = (cellValue: any) => {
    const normalizedValue = getCellBaseValue(cellValue);
    if (normalizedValue === null || normalizedValue === undefined) return '';
    return `${normalizedValue}`.trim();
};

const normalizeColorValue = (cellValue: any) => {
    const normalizedValue = getCellBaseValue(cellValue);
    if (typeof normalizedValue !== 'string') return normalizedValue;

    const trimmedValue = normalizedValue.trim();
    if (!trimmedValue) return '';

    return trimmedValue.startsWith('#') ? trimmedValue : `#${trimmedValue}`;
};

export const isWrappedCellValue = (cellValue: any) => {
    return (
        cellValue
        && typeof cellValue === 'object'
        && !Array.isArray(cellValue)
        && Object.prototype.hasOwnProperty.call(cellValue, 'value')
    );
};

export const getColumnDataKey = (colDef: any) => {
    return colDef?.field || colDef?.colId || colDef?.id;
};

export const resolveRowDataKeyFromParams = (params: any) => {
    const colDef = params?.colDef || params?.column?.getColDef?.();
    const candidateKeys = [
        colDef?.field,
        params?.column?.getColId?.(),
        colDef?.colId,
        colDef?.id,
    ].filter(Boolean);

    if (params?.data) {
        for (const key of candidateKeys) {
            if (Object.prototype.hasOwnProperty.call(params.data, key)) return key;
        }
    }

    return candidateKeys[0];
};

export const getRawCellValueFromParams = (params: any) => {
    const key = resolveRowDataKeyFromParams(params);
    if (key && params?.data && Object.prototype.hasOwnProperty.call(params.data, key)) return params.data[key];
    return params?.value;
};

export const getWarningFlagFromParams = (params: any) => {
    const rawValue = getRawCellValueFromParams(params);
    if (isWrappedCellValue(rawValue)) {
        return rawValue.isWarning === true;
    }
};

export const isCellEditableFromParams = (params: any, props: NbisTableProps) => {
    if (params?.data?.flag_disabled) return false;
    if (!props.editable) return false;
    if (props.allowCellDisabling) {
        const rawValue = getRawCellValueFromParams(params);
        if (isWrappedCellValue(rawValue)) {
            const hasIsDisable = Object.prototype.hasOwnProperty.call(rawValue, 'isDisable');
            const hasIsDisableCapital = Object.prototype.hasOwnProperty.call(rawValue, 'IsDisable');
            const isDisable = hasIsDisable
                ? Boolean(rawValue.isDisable)
                : (hasIsDisableCapital ? Boolean(rawValue.IsDisable) : false);
            return !isDisable;
        }
    }

    return true;
};

export const applyEditedValueToParams = (params: any) => {
    const key = resolveRowDataKeyFromParams(params) || getColumnDataKey(params?.colDef);
    if (!key || !params?.data) return false;

    const isColorColumn = params?.colDef?.dataType === 'color';
    const hasWrappedNewValue = isWrappedCellValue(params.newValue);
    const normalizedNewValue = isColorColumn
        ? normalizeColorValue(hasWrappedNewValue ? params.newValue?.value : params.newValue)
        : (hasWrappedNewValue ? params.newValue?.value : params.newValue);

    const existingValue = params.data[key];
    if (isWrappedCellValue(existingValue)) {
        // Preserve extra metadata keys on wrapped cells (e.g. isWarning).
        params.data[key] = hasWrappedNewValue
            ? { ...existingValue, ...params.newValue, value: normalizedNewValue }
            : { ...existingValue, value: normalizedNewValue };
        return true;
    }

    if (hasWrappedNewValue) {
        params.data[key] = isColorColumn
            ? { ...params.newValue, value: normalizedNewValue }
            : params.newValue;
        return true;
    }

    params.data[key] = normalizedNewValue;
    return true;
};

export const enhanceColumnDef = (item: any) => {
    if (['action', 'frontaction', 'dragCol', 'selectionCol'].includes(item?.colId)) return item;

    if (!item.valueGetter) {
        item.valueGetter = (params: any) => {
            const rawValue = getRawCellValueFromParams(params);
            if (item?.dataType === 'color') return normalizeColorValue(rawValue);
            return getCellBaseValue(rawValue);
        };
    }

    if (!item.valueSetter) {
        item.valueSetter = (params: any) => applyEditedValueToParams(params);
    }

    if (!item.getQuickFilterText) {
        item.getQuickFilterText = (params: any) => getCellDisplayText(getRawCellValueFromParams(params));
    }

    return item;
};

/* istanbul ignore next */
export const getColumnFilterByDataType = (dataType: string) => {
    switch (dataType) {
        case 'text':
            return 'agTextColumnFilter';
        case 'select':
            return 'agTextColumnFilter';
        case 'dateTime':
            return 'agDateColumnFilter';
        case 'boolean':
            return 'agSetColumnFilter';
        case 'date':
            return 'agDateColumnFilter';
        case 'color':
            return 'agSetColumnFilter';
        case 'number':
            return 'agNumberColumnFilter';
        default:
            return 'agTextColumnFilter';
    }
};

/* istanbul ignore next */
export const sortingComparator = (valueA: any, valueB: any, nodeA: any, nodeB: any, currentColumn: any, isInverted: any) => {
    /*let tempValueA;
    let tempValueB;

    if (valueA && typeof valueA === 'object' && 'value' in valueA) {
        tempValueA = valueA.value;
    } else {
        tempValueA = valueA;
    }

    if (valueB && typeof valueB === 'object' && 'value' in valueB) {
        tempValueB = valueB.value;
    } else {
        tempValueB = valueB;
    }

    if (!tempValueA) return -1;
    if (!tempValueB) return 1;
    if (typeof tempValueA === 'string' && typeof tempValueB === 'string') {
        return tempValueA?.toLowerCase()?.localeCompare(tempValueB?.toLowerCase());
    } else if (typeof tempValueA === 'number' && typeof tempValueB === 'number') {
        return tempValueA - tempValueB;
    } else {
        let valA = typeof tempValueA === 'string' ? tempValueA : moment(tempValueA).format('YYYY-MM-DD MM:HH');
        let valB = typeof tempValueB === 'string' ? tempValueB : moment(tempValueB).format('YYYY-MM-DD MM:HH');
        return valA?.toLowerCase()?.localeCompare(valB?.toLowerCase());
    }*/
    const compareGroupNode = (nodeA: any, nodeB: any, colId: string): number => {
        // Get the value of the current node (group field value)
        let currentNodeValueA = nodeA?.key;  // The key of group node is the group value
        let currentNodeValueB = nodeB?.key;

        // If no key, try to get from data
        if (currentNodeValueA === undefined && nodeA?.data) {
            currentNodeValueA = nodeA.data[colId];
        }
        if (currentNodeValueB === undefined && nodeB?.data) {
            currentNodeValueB = nodeB.data[colId];
        }

        // Handle cases where value might be an object
        if (currentNodeValueA && typeof currentNodeValueA === 'object' && 'value' in currentNodeValueA) {
            currentNodeValueA = currentNodeValueA.value;
        }
        if (currentNodeValueB && typeof currentNodeValueB === 'object' && 'value' in currentNodeValueB) {
            currentNodeValueB = currentNodeValueB.value;
        }

        // Compare current group node values
        const currentResult = compareValues(currentNodeValueA, currentNodeValueB);

        // If current node values are not equal, return directly
        if (currentResult !== 0) {
            return currentResult;
        }

        // If current node values are equal, compare child groups
        if (nodeA?.childrenAfterGroup && nodeB?.childrenAfterGroup && nodeA.childrenAfterGroup.length > 0) {
            const childrenA = nodeA.childrenAfterGroup;
            const childrenB = nodeB.childrenAfterGroup;

            for (let i = 0; i < Math.min(childrenA.length, childrenB.length); i++) {
                // Recursively compare child nodes
                const childResult = compareGroupNode(childrenA[i], childrenB[i], colId);
                if (childResult !== 0) {
                    return childResult;
                }
            }

            // Different number of child groups
            if (childrenA.length !== childrenB.length) {
                return childrenA.length - childrenB.length;
            }
        }

        return 0;
    };

    // Basic comparison logic
    const compareValues = (a: any, b: any) => {
        if (!a && b) return -1;
        if (a && !b) return 1;
        if (!a && !b) return 0;

        if (typeof a === 'string' && typeof b === 'string') {
            return a?.toLowerCase()?.localeCompare(b?.toLowerCase());
        } else if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        } else {
            try {
                let valA = typeof a === 'string' ? a : moment(a).format('YYYY-MM-DD HH:mm');
                let valB = typeof b === 'string' ? b : moment(b).format('YYYY-MM-DD HH:mm');
                return valA?.toLowerCase()?.localeCompare(valB?.toLowerCase());
            } catch (error) {
                // If conversion fails, fall back to string comparison
                return String(a)?.toLowerCase()?.localeCompare(String(b)?.toLowerCase());
            }
        }
    };

    // Check if current column is a group column
    const isGroupColumn = currentColumn?.rowGroup && currentColumn?.rowGroup === true && currentColumn?.enableRowGroup === true;
    // If it's a group column and has childrenAfterGroup, use group sorting
    if (isGroupColumn && (nodeA?.childrenAfterGroup !== undefined || nodeB?.childrenAfterGroup !== undefined) && currentColumn) {
        return compareGroupNode(nodeA, nodeB, currentColumn.colId);
    }

    // Normal row data comparison
    let tempValueA = valueA;
    let tempValueB = valueB;

    if (tempValueA && typeof tempValueA === 'object' && 'value' in tempValueA) {
        tempValueA = tempValueA.value;
    }
    if (tempValueB && typeof tempValueB === 'object' && 'value' in tempValueB) {
        tempValueB = tempValueB.value;
    }

    return compareValues(tempValueA, tempValueB);
};

/* istanbul ignore next */
const onActionButtonHandler = (item: any, rowData: any, params: any, props: NbisTableProps) => {
    return (
        item.id && props[item.id] && props[item.id]({ rowData: rowData, item: item }),
        params?.api?.redrawRows()
    );
};

/* istanbul ignore next */
const onDelete = (params: any, props: NbisTableProps) => {
    params?.api?.applyTransaction({ remove: [params.node.data] });
    return props.onDelete && props.onDelete(params.node.data);
};



/* istanbul ignore next */
export const initColumnBodyTemplate = (params: any, props: NbisTableProps) => {
    const { editMode, showDeleteButton, editable, showActionButtons, columns } = props;
    const column: any = params.colDef;
    const cellValue: any = getCellBaseValue(params.value);
    const isRowDisabled = params?.data?.flag_disabled === true;
    const fieldDataTypeMap = new Map<string, string>(
        columns?.map((column) => [column.field, column.dataType])
    );
    const fieldName: string = params.node.field;
    //add for pivot table
    if (typeof cellValue === 'object' && cellValue !== null && 'value' in cellValue) {
        return cellValue.value;
    }

    //add for pivot group column
    if (column.colId === "ag-Grid-AutoColumn") {
        if (fieldDataTypeMap.get(fieldName) === 'dateTime') {
            if (column.valueFormatter) {
                return column.valueFormatter(params);
            }
            const val: any = moment(cellValue)?.format('DD/MM/YYYY HH:mm');
            return val === 'Invalid date' ? '' : val;
        } else if (fieldDataTypeMap.get(fieldName) === 'date') {
            if (column.valueFormatter) {
                return column.valueFormatter(params);
            }
            let val = moment(cellValue)?.format('DD/MM/YYYY');
            return val === 'Invalid date' ? '' : val;
        }
        return cellValue;
    }

    if (column.dataType === 'dateTime') {
        if (column.valueFormatter) {
            return column.valueFormatter(params);
        }
        const val: any = moment(cellValue)?.format('DD/MM/YYYY HH:mm');
        return val === 'Invalid date' ? '' : val;
    } else if (column.dataType === 'date') {
        if (column.valueFormatter) {
            return column.valueFormatter(params);
        }
        let val = moment(cellValue)?.format('DD/MM/YYYY');
        return val === 'Invalid date' ? '' : val;
    } else if (column.dataType === 'color') {
        return <ColorPicker label="" value={normalizeColorValue(cellValue)} toolTipText="Help Icon" disabled={true} />;
    } else if (column?.dataType === 'select') {
        let option = column?.options?.filter((item: any) => item.value === cellValue)[0];
        return option ? option?.label : '';
    } else if (column.dataType === 'boolean') {
        return cellValue && <IconButton iconClassName='tick-icon' fileName="Icon-tick" size="medium" tooltipDisable={true} />;
    } else if (column.colId === 'frontaction') {
        return (<div>
            {params.data?.frontActionButtons?.map(((item: any) => {
                return (<IconButton key={item?.id} fileName={item?.icon} size="medium"
                    {...item.disableTooltip ? { tooltipDisable: true } : { tooltipDisable: false }}
                    toolTipArrow={item.toolTipArrow ? item.toolTipArrow : false}
                    toolTipPlacement={item?.toolTipPlacement || 'left'}
                    toolTipText={item?.title}
                    onClick={() => onActionButtonHandler(item, params.data, params, props)}
                />);
            }))}
        </div>);
    } else if (column.colId === 'dragCol') {
        return '';
    } else if (column.colId === 'selectionCol') {
        return '';
    } else if (column.dataType === 'text' || column.dataType === 'number') {
        if (column.valueFormatter) {
            return column.valueFormatter(params);
        }
        return cellValue;
    } else {
        let editingCells = params?.api?.getEditingCells();
        let isCurrentRowEditing = editingCells?.some((cell: any) => {
            return cell.rowIndex === params.node.rowIndex;
        });
        if (isCurrentRowEditing && props.editMode === 'row') {
            return (
                <div className='edit-icons'>
                    <IconButton fileName="Icon-tick" disabled={isRowDisabled} size="medium" tooltipDisable={true} onClick={() => params?.api?.stopEditing(false)} />
                    <IconButton fileName="Icon-cross" disabled={isRowDisabled} size="medium" tooltipDisable={true} onClick={() => params?.api?.stopEditing(true)} />
                </div>);
        } else {
            return (
                <div className='actionIcons'>
                    {editMode === 'row' && editable && <IconButton fileName="Icon-pen" size="medium" tooltipDisable={true}
                        disabled={isRowDisabled}
                        onClick={() => {
                            let filteredColumns = params?.api?.getDisplayedCenterColumns()?.filter((i: any) => i?.colId !== 'dragCol' && i?.colId !== 'selectionCol')!;
                            params?.api?.startEditingCell({
                                rowIndex: params.node.rowIndex,
                                colKey: filteredColumns[0]?.colId,
                            });
                        }} />}
                    {showDeleteButton && <IconButton fileName="Icon-trash" size="medium" tooltipDisable={true} disabled={isRowDisabled} onClick={() => onDelete(params, props)} />}
                    {showActionButtons && params.data?.actionButtons?.map(((item: any) => {
                        return (<IconButton key={item?.id} fileName={item?.icon} size="medium"
                            {...item.disableTooltip ? { tooltipDisable: true } : { tooltipDisable: false }}
                            toolTipArrow={item.toolTipArrow ? item.toolTipArrow : false}
                            toolTipPlacement={item?.toolTipPlacement || 'left'}
                            toolTipText={item?.title}
                            disabled={isRowDisabled}
                            onClick={() => onActionButtonHandler(item, params.data, params, props)}
                        />);
                    }))}
                </div>);
        }
    }
};

/* istanbul ignore next */
export const colorFilterItemTemplate = (option: any) => {
    const colorValue = normalizeColorValue(option?.value);
    return (
        <ColorPickerWrapper>
            <ColorCircle style={{ backgroundColor: colorValue }}></ColorCircle>
            <ColorLabel>{colorValue}</ColorLabel>
        </ColorPickerWrapper>
    );
};

/* istanbul ignore next */
export const booleanFilterItemTemplate = (option: any) => {
    if (option.value === 'true') {
        return <IconButton size='small' tooltipDisable={true} fileName='Icon-tick' />;
    } else if (option.value === 'false') {
        return '(Blank)';
    } else {
        return option.value;
    }
};

/* istanbul ignore next */
export const initFilterTemplate = (params: any) => {
    const column: any = params.colDef || params;

    if (column.dataType === 'select') {
        const filterOptions: ITextFilterParams =
        {
            buttons: ['clear'],
            defaultOption: 'contains',
            textMatcher: ({ filterOption, value, filterText }: { filterOption: any, value: any, filterText: any }) => {
                const filterTextLowerCase = filterText?.toLowerCase();
                let x: any = column?.options?.filter((option: any) => option?.value?.toLowerCase() === value)[0]?.label;
                const valueLowerCase = x?.toString()?.toLowerCase();
                switch (filterOption) {
                    case 'contains': return valueLowerCase.indexOf(filterTextLowerCase) >= 0;
                    case 'notContains': return valueLowerCase.indexOf(filterTextLowerCase) === -1;
                    case 'equals': return valueLowerCase === filterTextLowerCase;
                    case 'notEqual': return valueLowerCase !== filterTextLowerCase;
                    case 'startsWith': return valueLowerCase.indexOf(filterTextLowerCase) === 0;
                    case 'endsWith':
                        var index = valueLowerCase.lastIndexOf(filterTextLowerCase);
                        return index >= 0 && index === (valueLowerCase.length - filterTextLowerCase.length);
                    default: return false;
                }
            },
            maxNumConditions: 2,
        };
        return filterOptions;
    } else if (column.dataType === 'text') {
        const filterOptions: ITextFilterParams = {
            buttons: ['clear'],
            defaultOption: 'contains',
            maxNumConditions: 2,
        };
        return filterOptions;
    } else if (column.dataType === 'number') {
        const filterOptions: INumberFilterParams = {
            buttons: ['clear'],
            defaultOption: 'equals',
            maxNumConditions: 2,
        };
        return filterOptions;
    } else if (column.dataType === 'dateTime') {
        if (column?.cacheDataType === 'time') {
            const filterOptions: ITextFilterParams = {
                buttons: ['clear'],
                filterOptions: [`equals`, `notEqual`],
                defaultOption: 'equals',
                filterPlaceholder: "HH:mm",
                textMatcher: ({ filterOption, value, filterText }: { filterOption: any, value: any, filterText: any }) => {
                    let filterBy = value.substring(0, 5);
                    let filterMe = filterText;
                    if (filterBy === filterMe) {
                        return true;
                    }
                    return false;
                },
                maxNumConditions: 2,
            };
            return filterOptions;
        }
        const filterOptions: IDateFilterParams = {
            buttons: ['clear'],
            defaultOption: 'equals',
            filterOptions: ['equals', 'notEqual'],
            browserDatePicker: false,
            comparator: (filterLocalDate: Date, cellValue: any) => {
                let filterBy = moment(filterLocalDate).startOf('day').format('DD-MM-YYYY HH:mm');
                let filterMe = moment(cellValue).startOf('day').format('DD-MM-YYYY HH:mm');
                if (filterBy === filterMe) {
                    return 0;
                }
                if (filterMe < filterBy) {
                    return -1;
                }
                if (filterMe > filterBy) {
                    return 1;
                }
                return 0;
            },
            maxNumConditions: 2,
        };
        return filterOptions;
    } else if (column.dataType === 'date') {
        const filterOptions: IDateFilterParams = {
            buttons: ['clear'],
            defaultOption: 'equals',
            filterOptions: ['equals', 'notEqual'],
            comparator: (filterLocalDate: any, cellValue: any) => {
                filterLocalDate = new Date(filterLocalDate);
                cellValue = new Date(cellValue);
                let filterBy = moment(filterLocalDate).format('DD-MM-YYYY');
                let filterMe = moment(cellValue).format('DD-MM-YYYY');
                if (filterBy === filterMe) {
                    return 0;
                }
                if (filterMe < filterBy) {
                    return -1;
                }
                if (filterMe > filterBy) {
                    return 1;
                }
                return 0;
            },
            maxNumConditions: 2,
        };
        return filterOptions;
    } else if (column.dataType === 'color') {
        const filterOptions: ISetFilterParams = {
            buttons: ['clear'],
            cellRenderer: colorFilterItemTemplate,
        };
        return filterOptions;
    } else if (column.dataType === 'boolean') {
        const filterOptions: ISetFilterParams =
        {
            cellRenderer: booleanFilterItemTemplate,
            suppressMiniFilter: true,
        };
        return filterOptions;
    } else {
        const filterOptions: ITextFilterParams =
        {
            buttons: ['clear'],
            maxNumConditions: 2,
        };
        return filterOptions;
    }
};

/* istanbul ignore next */
export const onRowEditingStarted = (params: any) => {
    params?.api?.refreshCells({
        columns: ['action'],
        rowNodes: [params.node],
        force: true,
    });
};

/* istanbul ignore next */
export const onRowEditingStopped = (params: any) => {
    params?.api?.refreshCells({
        columns: ['action'],
        rowNodes: [params.node],
        force: true,
    });
};

/* istanbul ignore next */
export const onCellEditingStopped = (params: any, data: any, setData: any, onTableDataChanged?: (data: any[]) => void) => {
    if (params.valueChanged) {
        params.data.flag_edit = true;
        let newData: any = {};
        newData = data?.map((item: any, i: any) => {
            if (params.rowIndex === i) {
                item.isLastEditing = true;
                return params.data;
            }
            item.isLastEditing = false;
            return item;
        });
        setData(newData);

        // Notify parent component about data change
        if (onTableDataChanged && typeof onTableDataChanged === 'function') {
            onTableDataChanged(newData);
        }
        params?.api?.redrawRows();
        params?.api?.ensureIndexVisible(params.rowIndex, 'top');
    }
};